import React, { useEffect, useReducer, useRef } from 'react';
import {
  Button, Form, FormInstance, Loader, SelectPicker, Toggle,
} from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { showSuccessMessage } from '../../../../../utils/message';
import { ALL_COUNTRIES } from '../../../../../utils/countries';
import { loadWarehouseLocality, saveWarehouseLocality } from './kauflandWarehouseService';
import { initialState, reducer } from './kauflandWarehouseState';
import validationModel from './kauflandWarehouseValidation';

const SAVED_MESSAGE = 'Warehouse successfully saved.';

interface Props {
  marketplaceCode: string;
  warehouseLocalityId?: number;
}

export default function EshopSetupKauflandWarehouseEdit(
  { marketplaceCode, warehouseLocalityId }: Props,
) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const navigate = useNavigate();
  const formRef = useRef<FormInstance>(null);

  async function fetchWarehouseLocality(id: number): Promise<void> {
    dispatch({ type: 'status/set', payload: 'loading' });

    try {
      const warehouseLocality = await loadWarehouseLocality(id);
      dispatch({ type: 'data/loaded', payload: { warehouseLocality } });
    } finally {
      dispatch({ type: 'status/set', payload: 'ready' });
    }
  }

  useEffect(() => {
    formRef.current?.cleanErrors();

    if (!warehouseLocalityId) {
      return;
    }

    fetchWarehouseLocality(warehouseLocalityId).then();
  }, [warehouseLocalityId]);

  async function handleSave(): Promise<void> {
    if (!formRef.current?.check()) {
      return;
    }

    dispatch({ type: 'status/set', payload: 'saving' });

    try {
      const id = await saveWarehouseLocality(
        marketplaceCode,
        state.data.warehouseLocality,
        state.edit,
      );

      showSuccessMessage(SAVED_MESSAGE);

      if (id) {
        navigate(`${id}`, { replace: true });
      }
    } finally {
      dispatch({ type: 'status/set', payload: 'ready' });
    }
  }

  function handleCancel() {
    dispatch({ type: 'edit/reset' });
  }

  return state.status === 'loading' ? <Loader /> : (
    <Form
      fluid
      formValue={state.edit}
      onChange={(edit) => dispatch({ type: 'edit/set', payload: edit })}
      model={validationModel}
      layout="vertical"
      ref={formRef}
    >
      <div style={{ display: 'inline-grid', gridGap: '0px 20px', gridTemplateColumns: 'auto auto' }}>
        <Form.Group style={{ gridColumnStart: 1, gridColumnEnd: 3, width: 'fit-content' }}>
          <Form.ControlLabel>Warehouse name</Form.ControlLabel>
          <Form.Control name="name" type="text" />
          <Form.HelpText>Required</Form.HelpText>
        </Form.Group>
        <Form.Group>
          <Form.ControlLabel>Street</Form.ControlLabel>
          <Form.Control name="street" type="text" />
          <Form.HelpText>Required</Form.HelpText>
        </Form.Group>
        <Form.Group>
          <Form.ControlLabel>House number</Form.ControlLabel>
          <Form.Control name="houseNumber" type="text" />
          <Form.HelpText>Required</Form.HelpText>
        </Form.Group>
        <Form.Group>
          <Form.ControlLabel>City</Form.ControlLabel>
          <Form.Control name="city" type="text" />
          <Form.HelpText>Required</Form.HelpText>
        </Form.Group>
        <Form.Group>
          <Form.ControlLabel>Postcode</Form.ControlLabel>
          <Form.Control name="postcode" type="text" />
          <Form.HelpText>Required</Form.HelpText>
        </Form.Group>
        <Form.Group>
          <Form.ControlLabel>Country</Form.ControlLabel>
          <Form.Control
            name="countryCode"
            accepter={SelectPicker}
            data={ALL_COUNTRIES}
            labelKey="name"
            valueKey="code"
            block
          />
          <Form.HelpText>Required</Form.HelpText>
        </Form.Group>
        <Form.Group>
          <Form.ControlLabel>Phone</Form.ControlLabel>
          <Form.Control name="phone" type="tel" />
        </Form.Group>
        <Form.Group style={{ gridColumnStart: 1, gridColumnEnd: 3, width: 'fit-content' }}>
          <Form.ControlLabel>Default warehouse</Form.ControlLabel>
          <Form.Control
            name="isDefault"
            accepter={Toggle}
            checked={state.edit.isDefault}
          />
          <Form.HelpText>Kaufland requires exactly one default warehouse.</Form.HelpText>
        </Form.Group>
        <Button
          style={{ width: 'fit-content', justifySelf: 'end' }}
          appearance="primary"
          onClick={() => handleSave()}
          type="submit"
          loading={state.status === 'saving'}
        >
          Save
        </Button>
        <Button
          style={{ width: 'fit-content', justifySelf: 'start' }}
          appearance="ghost"
          onClick={() => handleCancel()}
        >
          Cancel
        </Button>
      </div>
    </Form>
  );
}

EshopSetupKauflandWarehouseEdit.defaultProps = {
  warehouseLocalityId: undefined,
};
